import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, useLang, formatApiError } from "../lib/providers";
import { Btn, Field, TextInput, TextArea } from "./primitives";
import { useConfirm } from "./ConfirmDialog";
import { Plus, PencilSimple, Trash, Star, Percent } from "@phosphor-icons/react";

/**
 * Self-contained VAT-rates CRUD section.
 * Used in SettingsPage as a sub-section (admin only).
 */
export default function VatRatesManager() {
  const { lang } = useLang();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/vat-rates").then((r) => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const startNew = () => setEditing({ label: "", rate: 19, note: "", is_default: false });

  const save = async (e) => {
    e.preventDefault();
    const payload = {
      label: editing.label.trim(),
      rate: Number(editing.rate) || 0,
      note: editing.note || "",
      is_default: !!editing.is_default,
    };
    if (!payload.label) {
      toast.error(lang === "de" ? "Bezeichnung darf nicht leer sein" : "Label cannot be empty");
      return;
    }
    try {
      if (editing.id) await api.put(`/vat-rates/${editing.id}`, payload);
      else await api.post("/vat-rates", payload);
      setEditing(null);
      load();
      toast.success(lang === "de" ? "MwSt-Satz gespeichert" : "VAT rate saved");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const del = async (r) => {
    const ok = await confirm({
      title: lang === "de" ? `MwSt-Satz "${r.label}" löschen?` : `Delete VAT rate "${r.label}"?`,
      description: lang === "de"
        ? "Bestehende Angebote/Rechnungen behalten ihren gespeicherten Satz, neue Dokumente können ihn aber nicht mehr auswählen."
        : "Existing quotes/invoices keep their saved rate, but new documents can no longer pick it.",
      confirmText: lang === "de" ? "Löschen" : "Delete",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/vat-rates/${r.id}`);
      load();
      toast.success(lang === "de" ? "Gelöscht" : "Deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="settings-vat-section">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-sm bg-[var(--brand)]/10 text-[var(--brand)] shrink-0">
            <Percent size={18} />
          </div>
          <div>
            <h3 className="font-heading text-base font-semibold text-[var(--ink)]">
              {lang === "de" ? "Mehrwertsteuer-Sätze" : "VAT rates"}
            </h3>
            <p className="text-xs text-[var(--muted)] mt-1 max-w-lg leading-relaxed">
              {lang === "de"
                ? "Steuersätze, die in Angeboten und Rechnungen wählbar sind. Der Hinweistext wird auf den PDFs unter dem Steuersatz gedruckt."
                : "Rates available in quotes and invoices. The note is printed on PDFs underneath the VAT line."}
            </p>
          </div>
        </div>
        <Btn onClick={startNew} data-testid="vat-new" className="shrink-0"><Plus size={14} /> {lang === "de" ? "Neuer Satz" : "New rate"}</Btn>
      </div>

      {loading ? (
        <div className="text-[var(--muted)] text-sm">…</div>
      ) : items.length === 0 ? (
        <div className="text-sm text-[var(--muted)] py-4">
          {lang === "de" ? "Noch keine MwSt-Sätze hinterlegt." : "No VAT rates yet."}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="border-b border-[var(--border)] text-left">
                <th className="py-2 pr-2 label-overline w-12">{lang === "de" ? "Std." : "Def."}</th>
                <th className="py-2 pr-2 label-overline">{lang === "de" ? "Bezeichnung" : "Label"}</th>
                <th className="py-2 pr-2 label-overline w-20 text-right">%</th>
                <th className="py-2 pr-2 label-overline">{lang === "de" ? "Hinweistext (PDF)" : "Note (PDF)"}</th>
                <th className="py-2 w-24 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-[var(--border)] align-top" data-testid={`vat-row-${r.id}`}>
                  <td className="py-3 pr-2">
                    {r.is_default && <Star size={16} weight="fill" className="text-[var(--brand)]" />}
                  </td>
                  <td className="py-3 pr-2 font-medium text-[var(--ink)]">{r.label}</td>
                  <td className="py-3 pr-2 text-right font-mono">{r.rate.toLocaleString(lang === "de" ? "de-DE" : "en-US", { maximumFractionDigits: 2 })} %</td>
                  <td className="py-3 pr-2 text-[var(--muted)] text-xs whitespace-pre-wrap leading-relaxed">{r.note || "—"}</td>
                  <td className="py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => setEditing(r)} className="text-[var(--muted)] hover:text-[var(--ink)]" data-testid={`vat-edit-${r.id}`}>
                        <PencilSimple size={16} />
                      </button>
                      <button onClick={() => del(r)} className="text-[var(--muted-2)] hover:text-[#FF3B30]" data-testid={`vat-del-${r.id}`}>
                        <Trash size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[var(--surface)] border border-[var(--border)] rounded-sm w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
            <form onSubmit={save}>
              <div className="px-6 py-4 border-b border-[var(--border)]">
                <div className="label-overline">{lang === "de" ? "MwSt-Satz" : "VAT rate"}</div>
                <h2 className="font-heading text-xl font-semibold">
                  {editing.id ? (lang === "de" ? "Satz bearbeiten" : "Edit rate") : (lang === "de" ? "Neuer Satz" : "New rate")}
                </h2>
              </div>
              <div className="px-6 py-5 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  <Field label={lang === "de" ? "Bezeichnung" : "Label"} className="md:col-span-2">
                    <TextInput value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} placeholder="z.B. 19% Mehrwertsteuer" autoFocus data-testid="vat-edit-label" />
                  </Field>
                  <Field label={lang === "de" ? "Satz (%)" : "Rate (%)"}>
                    <TextInput type="number" step="0.01" min="0" value={editing.rate} onChange={(e) => setEditing({ ...editing, rate: e.target.value })} data-testid="vat-edit-rate" />
                  </Field>
                </div>
                <Field label={lang === "de" ? "Hinweistext (wird im PDF gedruckt)" : "Note (printed on PDF)"} hint={lang === "de" ? "Leer lassen, wenn kein Hinweis benötigt wird." : "Leave empty if no note is needed."}>
                  <TextArea rows={3} value={editing.note} onChange={(e) => setEditing({ ...editing, note: e.target.value })} placeholder={lang === "de" ? "z.B. Bei der Lieferung handelt es sich um eine steuerfreie Ausfuhrlieferung i.S.v. § 4 Nr. 1a i.V.m. § 6 UStG." : ""} data-testid="vat-edit-note" />
                </Field>
                <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
                  <input type="checkbox" checked={!!editing.is_default} onChange={(e) => setEditing({ ...editing, is_default: e.target.checked })} data-testid="vat-edit-default" />
                  <span>{lang === "de" ? "Standard-Satz für neue Angebote/Rechnungen" : "Default rate for new quotes/invoices"}</span>
                </label>
              </div>
              <div className="px-6 py-4 border-t border-[var(--border)] flex justify-end gap-2">
                <Btn variant="ghost" type="button" onClick={() => setEditing(null)} data-testid="vat-edit-cancel">{lang === "de" ? "Abbrechen" : "Cancel"}</Btn>
                <Btn type="submit" data-testid="vat-edit-save">{lang === "de" ? "Speichern" : "Save"}</Btn>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
